import {
  TerminalEntryKind,
  TerminalMachineStatus,
  TerminalQuestionStatus,
  TerminalSessionStatus,
} from '@this+that/models';

import {
  TerminalConnectionStatus,
  TerminalDotTone,
} from '../../../enums/terminal';
import type { TerminalSessionState } from '../../../store/Terminal/types';

export function resolveMachineStatus(
  connection: TerminalConnectionStatus,
): TerminalMachineStatus {
  switch (connection) {
    case TerminalConnectionStatus.ONLINE:
      return TerminalMachineStatus.ONLINE;
    case TerminalConnectionStatus.CONNECTING:
    case TerminalConnectionStatus.RECONNECTING:
      return TerminalMachineStatus.RECONNECTING;
    default:
      return TerminalMachineStatus.OFFLINE;
  }
}

export function resolveDotTone(
  connection: TerminalConnectionStatus,
  sessionStatus: TerminalSessionStatus,
): TerminalDotTone {
  if (sessionStatus === TerminalSessionStatus.ENDED) {
    return TerminalDotTone.IDLE;
  }
  if (sessionStatus === TerminalSessionStatus.WAITING_PERMISSION) {
    return TerminalDotTone.WARNING;
  }
  const machine = resolveMachineStatus(connection);
  if (machine === TerminalMachineStatus.ONLINE) {
    return TerminalDotTone.ONLINE;
  }
  return machine === TerminalMachineStatus.RECONNECTING
    ? TerminalDotTone.WARNING
    : TerminalDotTone.IDLE;
}

const TAB_STATUS_KEY_PREFIX = 'terminal.tabs.status.';
const SESSION_BAR_STATUS_KEY_PREFIX = 'terminal.sessionBar.status.';
const TAB_STATUS_WORKING = 'working';
const TAB_STATUS_IDLE = 'idle';
const TAB_STATUS_ENDED = 'ended';

export function resolveTabDotTone(
  connection: TerminalConnectionStatus,
  sessionStatus: TerminalSessionStatus,
): TerminalDotTone {
  if (sessionStatus === TerminalSessionStatus.WAITING_PERMISSION) {
    return TerminalDotTone.WARNING;
  }
  const machine = resolveMachineStatus(connection);
  if (
    sessionStatus === TerminalSessionStatus.ENDED ||
    machine === TerminalMachineStatus.OFFLINE
  ) {
    return TerminalDotTone.IDLE;
  }
  if (machine === TerminalMachineStatus.RECONNECTING) {
    return TerminalDotTone.WARNING;
  }
  return sessionStatus === TerminalSessionStatus.RUNNING
    ? TerminalDotTone.ONLINE
    : TerminalDotTone.IDLE;
}

export function resolveTabStatusKey(
  connection: TerminalConnectionStatus,
  sessionStatus: TerminalSessionStatus,
): string {
  if (sessionStatus === TerminalSessionStatus.ENDED) {
    return `${TAB_STATUS_KEY_PREFIX}${TAB_STATUS_ENDED}`;
  }
  const machine = resolveMachineStatus(connection);
  if (machine !== TerminalMachineStatus.ONLINE) {
    return `${SESSION_BAR_STATUS_KEY_PREFIX}${machine}`;
  }
  return `${TAB_STATUS_KEY_PREFIX}${
    sessionStatus === TerminalSessionStatus.RUNNING
      ? TAB_STATUS_WORKING
      : TAB_STATUS_IDLE
  }`;
}

export function isSessionInteractive(
  connection: TerminalConnectionStatus,
  sessionStatus: TerminalSessionStatus,
): boolean {
  return (
    connection === TerminalConnectionStatus.ONLINE &&
    sessionStatus !== TerminalSessionStatus.ENDED
  );
}

export function firstWord(command: string, fallback: string): string {
  return command.trim().split(/\s+/)[0] || fallback;
}

export function sessionNeedsYou(
  session: Pick<TerminalSessionState, 'summary' | 'entries'>,
): boolean {
  return (
    session.summary.status === TerminalSessionStatus.WAITING_PERMISSION ||
    session.entries.some(
      (entry) =>
        entry.kind === TerminalEntryKind.QUESTION &&
        entry.status === TerminalQuestionStatus.PENDING,
    )
  );
}
